import Color from "color";

import { BarPlot, ColorAA, useAATheme } from "@bagel-web/components";

import { AgentHistoryMoment } from "./AgentHistory";
import { CommonAgentMomentList } from "../types";

/**
 * The moment timeline: one bar per moment at its occurrence time, colored
 * for LLM failures and ancestor-session moments, with click-to-select.
 * Extracted from Agent History for reuse (the Playground's source picker).
 */
const AgentMomentTimeline = ({
  sessionId,
  momentId,
  momentList,
  onSelectMoment,
}: {
  // The viewed session: moments owned by other sessions in the clone chain
  // render dimmed as ancestors.
  sessionId: string;
  momentId: number | null;
  momentList?: CommonAgentMomentList;
  onSelectMoment: (moment: AgentHistoryMoment) => void;
}) => {
  const { isDarkMode } = useAATheme();
  const errorBarColor = ColorAA.pink;
  const defaultBarColor = isDarkMode ? ColorAA.lightBeige : ColorAA.grey;
  const ancestorBarColor = isDarkMode
    ? Color(ColorAA.beige).alpha(0.6).hexa()
    : ColorAA.lightGrey;

  const chartData =
    momentList?.moments.map((moment) => {
      let backgroundColor = defaultBarColor;
      if (moment.has_llm_failure) {
        backgroundColor = errorBarColor;
      } else if (moment.session_id !== sessionId) {
        backgroundColor = ancestorBarColor;
      }
      return {
        x: moment.occurred_at * 1000,
        y: 1,
        momentId: moment.moment_id,
        foreignSessionId:
          moment.session_id !== sessionId ? moment.session_id : undefined,
        backgroundColor,
      };
    }) || [];

  let selectedTimestamp = momentList?.moments.find(
    (moment) => moment.moment_id === momentId
  )?.occurred_at;
  if (selectedTimestamp) {
    selectedTimestamp *= 1000;
  }

  let startTime = momentList?.moments[0]?.occurred_at;
  if (startTime) {
    startTime = (startTime - 1) * 1000;
  }
  let endTime =
    momentList?.moments?.[momentList.moments.length - 1]?.occurred_at;
  if (endTime) {
    endTime = (endTime + 10) * 1000;
  }

  return (
    <BarPlot
      momentId={momentId}
      startTime={startTime}
      endTime={endTime}
      selectedTimestamp={selectedTimestamp}
      onClickElement={(index) => {
        const clickedMoment = momentList?.moments[index];
        if (clickedMoment) {
          onSelectMoment({
            momentId: clickedMoment.moment_id,
            foreignSessionId: clickedMoment.session_id,
          });
        }
      }}
      data={{
        datasets: [
          {
            animation: {
              duration: 0,
            },
            barThickness: 4,
            label: "Moments",
            backgroundColor: chartData.map((moment) => moment.backgroundColor),
            data: chartData,
          },
        ],
      }}
    ></BarPlot>
  );
};

export default AgentMomentTimeline;
